import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {UserReportReasonEnum} from '@colonist/game-base-types'
import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints'
import {UIComponentDropdownController, DropdownContent} from '../../../shared/scripts/ui_components/UIComponentDropdownController'
import {UIPopupReportPlayer} from '../../../shared/scripts/ui_popup/UIPopupReportPlayer'
import {UIProfileFriendsTableController} from '../../../shared/scripts/profile/UIProfileFriendsTableController'
import {UIUtilsFriends} from '../../../shared/scripts/ui_friends/UIUtilsFriends'
import {ViewPopupLogin} from '../../../shared/scripts/ui_popup/ui_popup_membership_incentive/ViewPopupLogin'
import {ClientUserState} from '../../../shared/scripts/ClientUserState'
import {UIProfileLinkController} from '../../../shared/scripts/profile/UIProfileLinkController'
import {doActionIfCanGiftMembership} from '../../../shared/scripts/store/StoreUtils'
import {AnalyticEventCategory, AnalyticEventType, GiftMembershipMethod} from '../../../../shared/entity_datas/EntityDatas'
import {CA} from '../../../shared/scripts/analytics/Analytics'
import {LobbyImage} from '@colonist/types'
import {navigateToPage} from '../ui_utils/ClientUtils.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'

export class UIProfileFriendsController {
    dropdownController: UIComponentDropdownController
    private profileDropdownContent: DropdownContent[] = []
    profileIsCurrentUser: boolean
    private userFriendsData: AjaxDataTypes.UserFriendsData
    private username: string

    private readonly profileLinkController: UIProfileLinkController

    private readonly friendStateDropdownContent: Record<string, DropdownContent> = {
        friend: {id: 'profile_dropdown_removeFriend', class: 'component-dropdown-red', text: {key: 'strings:profilePage.friends.removeFriend.title'}, action: () => void this.showRemoveFriendPopup()},
        notFriend: {id: 'profile_dropdown_add_friend', class: '', text: {key: 'strings:game.playerOptionsPopup.addFriend'}, action: () => void this.addFriendDropdownAction()},
        pending: {id: 'profile_dropdown_pending', class: 'component-dropdown-disabled', text: {key: 'strings:profilePage.friends.friendRequestSent'}},
    }

    private readonly dropdownContentStates: Record<string, DropdownContent[]>
    private readonly toggleVisibilityComponent: HTMLDivElement | undefined

    constructor(username: string, profileLinkController: UIProfileLinkController, toggleVisibilityComponent?: HTMLDivElement) {
        this.username = username
        this.profileLinkController = profileLinkController
        this.toggleVisibilityComponent = toggleVisibilityComponent

        void this.init()
    }

    private async init() {
        this.profileIsCurrentUser = this.isProfileCurrentUser()
        if(!this.profileIsCurrentUser) {
            this.hideFriendsTab()
            this.redirectIfFriendSectionIsActive()
        }

        this.userFriendsData = await UIUtilsFriends.getFriends(ClientUserState.userState)
        this.profileLinkController.friendsSectionLink.hasMadeAPICall = true
        await this.createDropdown()
        new UIProfileFriendsTableController(this.userFriendsData)
    }

    setUsername(username: string) {
        this.username = username
    }

    hideFriendsTab() {
        this.profileLinkController.friendsSectionLink.style.display = 'none'
    }

    showFriendsTab() {
        this.profileLinkController.friendsSectionLink.style.display = 'flex'
    }

    private redirectIfFriendSectionIsActive() {
        if(this.profileLinkController.activeLink != this.profileLinkController.friendsSectionLink) return
        navigateToPage(`/profile/${this.username}`)
    }

    private isProfileCurrentUser(): boolean {
        return ClientUserState.userState?.username.toLowerCase() == decodeURIComponent(this.username.toLowerCase())
    }

    async createDropdown() {
        const friendDropdownContent = this.getInitialDropdownContentForUserFriendsData()
        this.profileDropdownContent = this.getDropdownContent(friendDropdownContent)
        if(this.toggleVisibilityComponent == undefined) return
        const dropdownParentId = 'sticky_player_info_text_container'
        this.dropdownController = new UIComponentDropdownController(this.profileDropdownContent, dropdownParentId, this.profileIsCurrentUser, this.toggleVisibilityComponent)
        this.dropdownController.dropdownView.style.left = '0'
    }

    private getDropdownContent(friendDropdownContent: DropdownContent): DropdownContent[] {
        const dropdownContent: DropdownContent[] = []
        if(!UIUtilsEjsVariables.isUserOnDiscordMobile) dropdownContent.push({id: 'profile_dropdown_gift_membership', class: '', text: {key: 'strings:storePage.sendGift'}, action: () => this.sendGiftDropdownAction(), icon: LobbyImage.StoreGiftMembershipDark})
        dropdownContent.push({id: 'profile_dropdown_report', class: 'component-dropdown-red', text: {key: 'strings:game.endGame.report'}, action: () => void this.reportProfileDropdownAction()})
        if(ClientUserState.hasUser()) dropdownContent.push(friendDropdownContent)
        return dropdownContent
    }

    private getInitialDropdownContentForUserFriendsData(): DropdownContent {
        if(this.userFriendsData == undefined) return this.friendStateDropdownContent.notFriend
        if(this.userFriendsData.friends.some((friend) => friend.username == this.username)) return this.friendStateDropdownContent.friend
        if(this.userFriendsData.friendRequestsSent.includes(this.username)) return this.friendStateDropdownContent.pending
        return this.friendStateDropdownContent.notFriend
    }

    private sendGiftDropdownAction() {
        const giftButtonClickEventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.GiftMembershipOverlay,
            floatValue: GiftMembershipMethod.PlayerProfile,
        }
        void CA.sendAnalyticEvent(giftButtonClickEventData)
        doActionIfCanGiftMembership(() => navigateToPage(`/store?username=${this.username}#gift`, true), this.username)
    }

    private async addFriendDropdownAction() {
        const updateDropdownOnSuccess = () => {
            const dropdownElement = document.getElementById('profile_dropdown_add_friend')
            if(dropdownElement == undefined) return
            dropdownElement.remove()

            this.dropdownController.createDropdownContentElement(this.friendStateDropdownContent.pending)
        }
        void UIUtilsFriends.addFriendAction(decodeURIComponent(this.username), updateDropdownOnSuccess)
    }

    private showRemoveFriendPopup() {
        const removeFriendPopupAction = () => {
            const updateDropdownOnSuccess = () => {
                const dropdownElement = document.getElementById('profile_dropdown_removeFriend')
                if(dropdownElement == undefined) return
                dropdownElement.remove()

                this.dropdownController.createDropdownContentElement(this.friendStateDropdownContent.notFriend)
            }
            void UIUtilsFriends.removeFriendAction(this.username, updateDropdownOnSuccess)
        }
        UIUtilsFriends.showRemoveFriendPopup(this.username, removeFriendPopupAction)
    }

    private async reportProfileDropdownAction() {
        if(!ClientUserState.userState?.isLoggedIn) {
            new ViewPopupLogin()
            return
        }
        const reportPopupAction = (playerName: string, reportReason: UserReportReasonEnum) => {
            const userReportData: AjaxDataTypes.UserReportData = {reason: reportReason}
            void UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiReportPlayer(playerName), undefined, userReportData, 'application/json')
        }
        UIPopupReportPlayer.createReportPopup(this.username, reportPopupAction, {key: 'strings:popups.reportPopup.success'})
    }
}
